import React from "react";
import PropTypes from "prop-types";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { Grid, InputAdornment } from "@mui/material";
import { format, isValid } from "date-fns";

import {
  NUMBER_REGEX,
  DECIMAL_REGEX,
  blockMinus,
  limitDigits
} from "../../../../../../utils/validation";
import { getInvalidMessage } from "../../../../../../utils/getValidationMessage";
import { DATE_FORMAT } from "../../../../../../constants";
import { DirhamSymbol } from "../../../../../../components/DirhamSymbol";
import {
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker
} from "../../../../../../components/Form";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { Divider } from "../../../../../../components/Divider";
import { ButtonWrapper, RemoveButton } from "./styled";
import { documentUnique, downloadableDocsData } from "../../../../../../constants/documents";
import { RoDocument } from "../../../../../UploadDocuments/components/RoDocument/RoDocument";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

const inwardChequeSchema = () =>
  Yup.object().shape({
    cheques: Yup.array().of(
      Yup.object().shape({
        inwardChequeCount6Month: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(
            NUMBER_REGEX,
            getInvalidMessage("Total count of inward cheque returns in last 6 months")
          ),
        inwardChequeAmount6Month: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(
            DECIMAL_REGEX,
            getInvalidMessage("Amount of inward cheque returns in last 6 months")
          ),
        inwardChequeCount12Month: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(
            NUMBER_REGEX,
            getInvalidMessage("Total count of inward cheque returns in last 12 months")
          ),
        inwardChequeAmount12Month: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(
            DECIMAL_REGEX,
            getInvalidMessage("Amount of inward cheque returns in last 12 months")
          ),
        chequeBouncedDate: Yup.date()
          .nullable()
          .max(new Date(), getInvalidMessage("Date of cheque bounced"))
          .typeError(getInvalidMessage("Date of cheque bounced")),
        chequeNumber: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("Cheque number")),
        bankName: Yup.string()
          .max(50, "Maximum 50 characters allowed")
          .nullable(),
        amount: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(DECIMAL_REGEX, getInvalidMessage("Amount")),
        clearedBy: Yup.string()
          .max(50, "Maximum 50 characters allowed")
          .nullable(),
        clearedDate: Yup.date()
          .nullable()
          .max(new Date(), getInvalidMessage("Cleared on"))
          .typeError(getInvalidMessage("Date of cheque bounced")),
        remarks: Yup.string()
          .max(120, "Maximum 120 characters allowed")
          .nullable()
      })
    )
  });

export const InwardChequesComponent = ({ handleContinue, updateRoProspect, inwardCheque }) => {
  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  return (
    <Formik
      initialValues={{
        cheques: inwardCheque.map(item => ({
          ...item
        }))
      }}
      onSubmit={handleContinue}
      validationSchema={inwardChequeSchema}
      validateOnChange={false}
    >
      {({ values }) => {
        const inwardCheques = values.cheques.length > 1;
        const amountInputProps = {
          endAdornment: (
            <InputAdornment position="end">
              <DirhamSymbol />
            </InputAdornment>
          ),
          inputProps: { min: 0, step: 0.01, tabIndex: 0, onKeyDown: blockMinus }
        };
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <FieldArray name="cheques">
                {inwardChequesArrayHelpers => (
                  <>
                    {values.cheques.map((item, index) => (
                      <React.Fragment key={item.chequeNumber || index}>
                        <Grid item sm={9} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].totalCount6`}
                            name={`cheques[${index}].inwardChequeCount6Month`}
                            label="Total count of inward cheque returns in last 6 months"
                            placeholder="Total count of inward cheque returns in last 6 months"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].amount6`}
                            name={`cheques[${index}].inwardChequeAmount6Month`}
                            label="Amount of inward cheque returns in last 6 months"
                            placeholder="Amount of inward cheque returns in last 6 months"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].totalCount12`}
                            name={`cheques[${index}].inwardChequeCount12Month`}
                            label="Total count of inward cheque returns in last 12 months"
                            placeholder="Total count of inward cheque returns in last 12 months"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].amount12`}
                            name={`cheques[${index}].inwardChequeAmount12Month`}
                            label="Amount of inward cheque returns in last 12 months"
                            placeholder="Amount of inward cheque returns in last 12 months"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].chequeBouncedDate`}
                            name={`cheques[${index}].chequeBouncedDate`}
                            label="Date of cheque bounced"
                            placeholder="Date of cheque bounced"
                            component={DatePicker}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            changeProspect={changeDateProspectHandler}
                            maxDate={new Date()}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].chequeNumber`}
                            name={`cheques[${index}].chequeNumber`}
                            label="Cheque number"
                            placeholder="Cheque number"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].bankName`}
                            name={`cheques[${index}].bankName`}
                            label="Bank Name"
                            placeholder="Bank Name"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].amount`}
                            name={`cheques[${index}].amount`}
                            label="Amount"
                            placeholder="Amount"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].clearedBy`}
                            name={`cheques[${index}].clearedBy`}
                            label="Cleared by"
                            component={SelectAutocomplete}
                            datalistId="inwardChequeClearedBy"
                            shrink
                            tabIndex="0"
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].clearedDate`}
                            name={`cheques[${index}].clearedDate`}
                            label="Cleared on"
                            placeholder="Cleared on"
                            component={DatePicker}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            changeProspect={changeDateProspectHandler}
                            maxDate={new Date()}
                          />
                        </Grid>
                        <Grid item sm={12} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.inwardCheque.[${index}].remarks`}
                            name={`cheques[${index}].remarks`}
                            label="Remarks"
                            placeholder="Remarks"
                            component={Input}
                          />
                        </Grid>
                        {inwardCheques && (
                          <Grid item sm={12} xs={12}>
                            <RemoveButton
                              arrayHelpers={inwardChequesArrayHelpers}
                              dataArray={inwardCheque}
                              itemIndex={index}
                              prospectPath="roProspect.organizationInfoRO.inwardCheque"
                              updateProspect={updateRoProspect}
                              title="Delete"
                            />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}
                    <Grid item sm={12} xs={12}>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={[]}
                        addedItem={{
                          chequeBouncedDate: "",
                          chequeNumber: "",
                          bankName: "",
                          amount: "",
                          clearedBy: "",
                          clearedDate: "",
                          remarks: ""
                        }}
                        dataArray={values.cheques}
                        arrayHelpers={inwardChequesArrayHelpers}
                      />
                    </Grid>
                  </>
                )}
              </FieldArray>
              <Grid item sm={12} xs={12}>
                <RoDocument uniqueId={documentUnique.InwardChequeProof} />
              </Grid>
              <Divider.Light />
              <Grid item sm={12} xs={12}>
                <RoDocument
                  uniqueId={documentUnique.InwardChequeTemplate}
                  downloadUrl={downloadableDocsData[documentUnique.InwardChequeTemplate].url}
                  downloadLabel={downloadableDocsData[documentUnique.InwardChequeTemplate].title}
                />
              </Grid>
            </Grid>
            <ButtonWrapper>
              <ContinueButton type="submit" />
            </ButtonWrapper>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

InwardChequesComponent.propTypes = {
  /**
   * Function to handle form submission and continue to the next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Function to update RO prospect data in Redux store
   */
  updateRoProspect: PropTypes.func.isRequired,

  /**
   * Array of inward cheque information objects
   */
  inwardCheque: PropTypes.arrayOf(
    PropTypes.shape({
      inwardChequeCount6Month: PropTypes.string,
      inwardChequeAmount6Month: PropTypes.string,
      inwardChequeCount12Month: PropTypes.string,
      inwardChequeAmount12Month: PropTypes.string,
      chequeBouncedDate: PropTypes.string,
      chequeNumber: PropTypes.string,
      bankName: PropTypes.string,
      amount: PropTypes.string,
      clearedBy: PropTypes.string,
      clearedDate: PropTypes.string,
      remarks: PropTypes.string
    })
  ).isRequired
};
